import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import DashboardLayout from '../components/DashboardLayout';
import { useCachedData } from '../hooks/useCachedData';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import { api, endpoints } from '../api/config';

const LOCATION_LABEL_OVERRIDES = {
  '9': 'L41 Flemington',
  '15': 'L60 Hardeeville',
};

const parseLocationsList = (locationsData) => {
  if (!locationsData) return [];
  const raw = locationsData;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (raw?.data && typeof raw.data === 'object') return Object.values(raw.data);
  if (Array.isArray(raw?.results)) return raw.results;
  if (Array.isArray(raw?.records)) return raw.records;
  if (typeof raw === 'object') return Object.values(raw);
  return [];
};

const buildLocationLabel = (loc) => {
  const id = loc?.id ?? loc?.location_id;
  if (id != null && LOCATION_LABEL_OVERRIDES[String(id)]) {
    return LOCATION_LABEL_OVERRIDES[String(id)];
  }
  return loc?.location || loc?.name || (id != null ? String(id) : '');
};

const LtlPalletMap = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const { data: locationsData, isLoading: locationsLoading } = useCachedData(endpoints.locations);

  const locationOptions = useMemo(() => {
    const options = parseLocationsList(locationsData)
      .map((loc) => {
        const value = String(loc?.location ?? loc?.name ?? loc?.id ?? loc?.location_id ?? '');
        const label = buildLocationLabel(loc);
        if (!value || !label) return null;
        return { value, label };
      })
      .filter(Boolean);

    return [...new Map(options.map((option) => [option.value, option])).values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [locationsData]);

  const apiLocationParam = useMemo(() => {
    if (!selectedLocation || selectedLocation === 'all') return null;
    return selectedLocation;
  }, [selectedLocation]);

  const {
    data: palletMapRows = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['wms_data', 'ltlPalletMap', apiLocationParam ?? 'all'],
    queryFn: async () => {
      const response = await api.get(endpoints.ltlPalletMap, {
        params: apiLocationParam ? { location: apiLocationParam } : undefined,
      });
      const payload = response.data?.data ?? response.data ?? [];
      return Array.isArray(payload) ? payload : [];
    },
    enabled: selectedLocation === 'all' || Boolean(apiLocationParam),
    staleTime: 30000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return palletMapRows;
    const term = searchTerm.trim().toLowerCase();
    return palletMapRows.filter((row) => JSON.stringify(row).toLowerCase().includes(term));
  }, [palletMapRows, searchTerm]);

  const orderCount = useMemo(() => {
    return new Set(filteredRows.map((row) => row.transaction_id).filter(Boolean)).size;
  }, [filteredRows]);

  const columns = useMemo(
    () => [
      { accessorKey: 'transaction_id', header: 'Transaction ID', minWidth: 140, width: 150 },
      { accessorKey: 'po_number', header: 'PO Number', minWidth: 130, width: 150 },
      { accessorKey: 'customer_name', header: 'Customer', minWidth: 150, width: 180 },
      { accessorKey: 'carrier_name', header: 'Carrier', minWidth: 120, width: 140 },
      { accessorKey: 'ship_date', header: 'Ship Date', minWidth: 110, width: 120 },
      { accessorKey: 'line_id', header: 'Line ID', minWidth: 120, width: 140 },
      { accessorKey: 'item', header: 'Item', minWidth: 120, width: 140 },
      { accessorKey: 'location', header: 'Location', minWidth: 120, width: 140 },
      { accessorKey: 'line_quantity', header: 'Line Qty', minWidth: 90, width: 100 },
      { accessorKey: 'unit_index', header: 'Unit #', minWidth: 80, width: 90 },
      { accessorKey: 'sscc_code', header: 'SSCC', minWidth: 220, width: 240 },
      { accessorKey: 'pallet_number', header: 'Pallet Number', minWidth: 220, width: 240 },
      { accessorKey: 'bin_number', header: 'Bin', minWidth: 100, width: 120 },
      { accessorKey: 'picker_name', header: 'Picker', minWidth: 120, width: 140 },
      { accessorKey: 'picked_time', header: 'Picked Time', minWidth: 150, width: 170 },
    ],
    []
  );

  const gridColumns = useMemo(
    () =>
      columns.map((col) => ({
        ...col,
        flex: 0,
        cell: (info) => {
          const value = info.getValue();
          if (value === null || value === undefined || value === '') return '-';
          return String(value);
        },
      })),
    [columns]
  );

  const palletMapGrid = useDataGridProTable({
    data: filteredRows,
    loading: isLoading,
    error: error?.message || null,
    title: 'LTL Pallets',
    columns: gridColumns,
    onDataRefresh: refetch,
    getRowId: (row) => String(row.id),
    hideQuickFilter: true,
    disablePaper: true,
    muiTableContainerProps: { sx: { height: 'calc(100vh - 280px)', minHeight: 420, minWidth: 0 } },
    gridSx: {
      width: '100%',
      minWidth: 0,
      '& .MuiDataGrid-virtualScroller': {
        overflowX: 'auto !important',
        overflowY: 'auto !important',
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        minWidth: 'max-content !important',
      },
    },
  });

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold', color: 'primary.main' }}>
          LTL Pallets
        </Typography>

        <Box sx={{ mb: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search PO, SSCC, pallet, item..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <TextField
            select
            size="small"
            label="Location"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            disabled={locationsLoading}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All Locations</MenuItem>
            {locationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {error?.message ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error.message}
          </Alert>
        ) : null}

        <Paper elevation={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              SSCC / Pallet Mapping
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isLoading
                ? 'Loading live LTL line-level pallet data...'
                : `${filteredRows.length} unit row(s) from ${orderCount} LTL order(s)`}
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, px: 0.5, pb: 0.5 }}>
              <MuiXDataGridSurface hook={palletMapGrid} DataGridPro={DataGridPro} />
            </Box>
          )}
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default LtlPalletMap;
